import { useRef, useEffect, MutableRefObject } from "react";
import { useClickable, AgTechWebClickable } from "../../../Buttons/AgTechButtons";
import AgTechDatePicker, { AgTechDatePickerChangeHandler } from "./AgTechDatePicker";
import dayjs from "dayjs";
import { AgTechPopUpPosition, AgTechPopUpCoreProps, AgTechRenderedPopUp } from "@agtech/core/functions/AgTechAppPopUps";

export const useAgTechDatePickerPopUp = (props: {
    inputRef: React.RefObject<HTMLInputElement>,
    dateValue: React.MutableRefObject<string>,
    dateChangedHandlerRef: React.MutableRefObject<AgTechDatePickerChangeHandler>,
    initialDateValue: string,
    onDateSelected: (date: string) => void
}) => {
    const inputRef = props.inputRef;
    const dateValue = props.dateValue;
    const handleDateChanged = props.onDateSelected;
    
    useAgTechPopUpOnFocus({
        callerRef: props.inputRef,
        popup: {
            popup: () => (
                <AgTechDatePicker
                    dateChangedHandlerRef={props.dateChangedHandlerRef}
                    callerRef={props.inputRef}
                    initialDateValue={props.initialDateValue}
                    onDateSelected={props.onDateSelected}
                />
            ),
            config: {
                position: AgTechPopUpPosition.BelowRight,
                fadeInTime: '200ms',
                shouldCloseOnBlur: false
            },
        }
    });

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.onkeydown = (e) => {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();

                    const updatedDate = dateValue.current
                        ? dayjs(dateValue.current).add(-1, 'day').format('MM/DD/YYYY')
                        : dayjs().add(-1, 'day').format('MM/DD/YYYY');

                    handleDateChanged(updatedDate);
                }
                else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    
                    const updatedDate = dateValue.current
                        ? dayjs(dateValue.current).add(1, 'day').format('MM/DD/YYYY')
                        : dayjs().add(1, 'day').format('MM/DD/YYYY');

                    handleDateChanged(updatedDate);
                }
            }
        }

        return () => {
            if (inputRef.current) {
                inputRef.current.onkeydown = null;
            }
        }
    }, []);
}


export type AgTechPopUpFocusProps = {
    callerRef: React.RefObject<HTMLInputElement>,
    popup: AgTechPopUpCoreProps,
    handlers?: {
        onPopupOpened?: () => void,
        onPopupClosed?: () => void
    },
}

export const useAgTechPopUpOnFocus = (props: AgTechPopUpFocusProps) => {
    const popupRef = useRef<AgTechRenderedPopUp | null>(null);
    const popupRefClickable  = useClickable(props.callerRef);

    useEffect(() => {
        if (props.callerRef.current) {
            props.callerRef.current.onfocus = () => handleCallerFocus(props, popupRef, popupRefClickable);
            props.callerRef.current.onblur = e => handleCallerBlur(e, popupRef);
        }
    });
}

const handleCallerFocus = (
    props: AgTechPopUpFocusProps,
    popupRef: MutableRefObject<AgTechRenderedPopUp | null>,
    callerRefClickable: AgTechWebClickable
) => {
    if (popupRef.current === null) {
        popupRef.current = callerRefClickable.popups.showPopUp({
            callerRef: props.callerRef,
            ...props.popup,
            handlers: {
                onPopUpOpened: () => {
                    if (props.handlers?.onPopupOpened) {
                        props.handlers.onPopupOpened();
                    }
                },
                onPopUpClosed: () => {
                    popupRef.current = null;

                    if (props.handlers?.onPopupClosed) {
                        props.handlers.onPopupClosed();
                    }
                }
            }
        });
    }
}

const handleCallerBlur = (
    callerBlurEvent: any,
    popupRef: MutableRefObject<AgTechRenderedPopUp | null>
) => {
    if (callerBlurEvent.relatedTarget && popupRef.current) {     
        if (callerBlurEvent.relatedTarget.parentElement.id === 'selection-header') {
            return;
        }

        if (popupRef.current && popupRef.current.popupRef.current &&
            popupRef.current.popupRef.current.contains(callerBlurEvent.relatedTarget)) {
            return;
        }

        popupRef.current.close();
        popupRef.current = null;
    }
}